/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { nativeFetchRef } from '@amazon/vinyl-util'
import { IS_TAURI } from '@/util/platform'

// Tauri's WebView, like a browser, enforces CORS. Several backends Bullfrog
// talks to (notably Stratus on `music.amazon.com`) reject the app origin
// and break under WKWebView. The Tauri HTTP plugin proxies requests through
// Rust (reqwest), bypassing the WebView's CORS enforcement entirely.
// Override Vinyl's `nativeFetchRef` so those backends' requests go through
// it. The override is no-op on web — leaving the browser's native `fetch`
// in place — so the web build's behavior is unchanged.
//
// Only hosts the plugin's capability scope allows
// (`packages/amp-tauri/src-tauri/capabilities/default.json`) go through it;
// the plugin rejects anything else. Media CDNs (CloudFront manifests and
// segments, podcast hosts) send `Access-Control-Allow-Origin: *`, so they stay
// on the WebView's fetch, which also keeps segment bytes off the Rust IPC
// bridge. Same-origin requests (i18n bundles, anything served by the `app://`
// URI scheme handler) stay there too: reqwest only speaks http/https.
//
// The plugin import is dynamic so the Tauri HTTP runtime ends up in its
// own lazily-loaded chunk and stays out of the web entry.

export async function initializeTauri() {
    if (!IS_TAURI) return
    const { fetch: pluginFetch } = await import('@tauri-apps/plugin-http')
    const builtinFetch = window.fetch.bind(window)
    nativeFetchRef.set(
        () => (input: RequestInfo | URL, init?: RequestInit) =>
            usesPlugin(input) ? pluginFetch(input, init) : builtinFetch(input, init)
    )
}

const PLUGIN_HOST_SUFFIXES = ['.amazon.com', '.amazon.dev']

function usesPlugin(input: RequestInfo | URL): boolean {
    const url = parseUrl(input)
    if (!url || url.origin === window.location.origin) return false
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return false
    const host = url.hostname
    return (
        host === 'localhost' ||
        host === 'amazon.com' ||
        PLUGIN_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix))
    )
}

function parseUrl(input: RequestInfo | URL): URL | null {
    try {
        if (input instanceof URL) return input
        return new URL(typeof input === 'string' ? input : input.url, window.location.href)
    } catch {
        return null
    }
}
